import { useEffect, useState } from 'react';

import { customersKey, useCustomers, type Customer } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass } from '@/components/Field';
import { PageLoader } from '@/components/PageLoader';
import { Button, Card } from '@/components/PageHeading';
import { useDebounced } from '@/components/useDebounced';
import { createCustomer, type QuickCustomerInput } from '@core/api/partyCommands';
import type { CustomerStepExit } from '@core/bookings/newBookingSteps';
import {
  highlight,
  newCustomerInput,
  newCustomerProblem,
  prefillFromSearch,
  type NewCustomer,
} from '@core/customers/picker';

type Tab = 'find' | 'new';

const empty: NewCustomer = { name: '', mobile: '', city: '', email: '' };

/**
 * Step 2 of a new booking: **who is paying**, as two tabs, Find a customer and New customer — the
 * phone's own (canvas "Booking flow: customer after dates", direction A, owner 2026-11-05). Choosing
 * somebody moves on by itself. The rules are the core's (`customers/picker.ts`), and the server still
 * refuses a number already on file, naming who has it.
 */
export function CustomerStep({
  branchId,
  exit,
  mayAdd,
  onPick,
  onExit,
}: {
  branchId: number | undefined;
  /** The way on without choosing anybody new (`customerStepExit`). */
  exit: CustomerStepExit;
  /** Whether this clerk may add a customer. Without it, New is not offered: the server would refuse. */
  mayAdd: boolean;
  onPick: (customer: Customer) => void;
  onExit: () => void;
}) {
  const [tab, setTab] = useState<Tab>('find');
  const [form, setForm] = useState<NewCustomer>(empty);

  const addInstead = (typed: string) => {
    setForm({ ...empty, ...prefillFromSearch(typed) });
    setTab('new');
  };

  return (
    <div className="mx-auto w-full max-w-[720px]">
      <Card>
        {mayAdd && (
          <div role="tablist" className="bg-light mb-4 grid grid-cols-2 gap-1 rounded-xl p-1">
            <TabButton label="Find a customer" on={tab === 'find'} onClick={() => setTab('find')} />
            <TabButton label="New customer" on={tab === 'new'} onClick={() => setTab('new')} />
          </div>
        )}

        {tab === 'find' ? (
          <FindPanel
            branchId={branchId}
            mayAdd={mayAdd}
            onPick={onPick}
            onAddInstead={addInstead}
          />
        ) : (
          <NewPanel form={form} onForm={setForm} onPick={onPick} />
        )}

        <div className="border-border-color mt-4 border-t pt-3 text-center">
          {exit.kind === 'keep' ? (
            <Button
              // Beside Add on the New tab, the one strong button stays Add.
              variant={tab === 'find' ? 'primary' : 'secondary'}
              onClick={onExit}
              className="h-11 justify-center px-5"
            >
              {exit.label}
              <i className="icon icon-arrow-right" />
            </Button>
          ) : (
            <button
              type="button"
              onClick={onExit}
              className="text-primary min-h-11 cursor-pointer border-0 bg-transparent px-2 text-[13px] font-semibold hover:underline"
            >
              {exit.kind === 'skip' ? `${exit.label} and choose them at the bag` : exit.label}
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}

function TabButton({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onClick}
      className={`min-h-11 cursor-pointer rounded-[9px] border-0 text-[14px] font-semibold ${
        // Important, or the template's dark `button[aria-selected='true']` paints it in the accent.
        on ? 'text-primary! bg-white! font-bold shadow-sm' : 'bg-transparent text-gray-900'
      }`}
    >
      {label}
    </button>
  );
}

function FindPanel({
  branchId,
  mayAdd,
  onPick,
  onAddInstead,
}: {
  branchId: number | undefined;
  mayAdd: boolean;
  onPick: (customer: Customer) => void;
  onAddInstead: (typed: string) => void;
}) {
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const focusFirst = useAutoFocusFirstField<HTMLDivElement>();
  const typed = term.trim();
  const asked = useDebounced(typed);
  const askable = asked.length >= 2;
  const results = useCustomers(branchId, { search: asked, pageSize: 10 });
  const found = askable ? (results.data?.items ?? []) : [];
  const total = askable ? (results.data?.total ?? 0) : 0;

  useEffect(() => setActive(0), [asked]);

  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((was) => Math.min(was + 1, found.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((was) => Math.max(was - 1, 0));
    } else if (event.key === 'Enter') {
      // Stays here rather than moving to the next field: Enter is the choice on this list.
      event.preventDefault();

      const picked = found[active];

      if (picked) {
        onPick(picked);
      } else if (mayAdd && askable && !results.isFetching) {
        onAddInstead(typed);
      }
    }
  };

  return (
    <div ref={focusFirst} data-enter-key-owns="" className="flex flex-col gap-3">
      <Field label="Mobile number or name">
        <div className="border-primary flex h-[50px] items-center gap-2.5 rounded-[10px] border-[1.5px] bg-white px-3.5">
          <i className="icon icon-search text-default" aria-hidden="true" />
          <input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={onKey}
            placeholder="At least two digits or letters"
            className="min-w-0 flex-1 border-0 bg-transparent py-0 text-[16px] text-gray-900 shadow-none outline-none focus:border-0 focus:shadow-none focus:ring-0 focus:outline-none"
          />
        </div>
      </Field>

      {!askable ? (
        <p className="text-default mb-0 text-[14px]">
          Type their mobile number or name.
          {mayAdd ? ' Someone new? Use the New customer tab.' : ''}
        </p>
      ) : results.isLoading ? (
        <PageLoader label="Finding customers" compact />
      ) : found.length === 0 ? (
        <div className="bg-light flex flex-col items-start gap-2.5 rounded-xl p-3.5">
          <span className="text-[14px]">Nobody on file matches “{asked}”.</span>
          {mayAdd && (
            <Button onClick={() => onAddInstead(asked)} className="h-11">
              Add “{asked}” as a new customer
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-default text-[12px] font-bold tracking-wider uppercase">
              {total === 1 ? '1 found' : `${total} found`}
            </span>
            <span className="text-default text-[12px] max-sm:hidden">
              Up and down arrows to move, Enter to choose
            </span>
          </div>
          <ul className="border-border-color m-0 list-none overflow-hidden rounded-xl border ps-0">
            {found.map((customer, index) => (
              <li key={customer.id} className="list-none">
                <button
                  type="button"
                  onMouseEnter={() => setActive(index)}
                  onClick={() => onPick(customer)}
                  className={`flex min-h-16 w-full cursor-pointer items-center gap-3 border-0 px-3 py-2.5 text-start ${
                    index === 0 ? '' : 'border-border-color border-t'
                  } ${index === active ? 'bg-light' : 'bg-white'}`}
                >
                  <span className="bg-primary-transparent text-primary flex size-10 shrink-0 items-center justify-center rounded-full text-[13px] font-bold">
                    {initials(customer.name)}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[15px] font-semibold text-gray-900">
                      <Marked text={customer.name} term={asked} />
                    </span>
                    <span className="text-default truncate text-[13px]">
                      <span className="tabular-nums">
                        <Marked text={customer.mobile} term={asked} />
                      </span>
                      {customer.city ? ` · ${customer.city}` : ''}
                      {' · '}
                      {rented(customer.rentalCount)}
                    </span>
                  </span>
                  <i className="icon icon-chevron-right text-default" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          {total > found.length && (
            <p className="text-default mb-0 text-[12px]">
              {total - found.length} more match. Keep typing to narrow it.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function NewPanel({
  form,
  onForm,
  onPick,
}: {
  form: NewCustomer;
  onForm: (form: NewCustomer) => void;
  onPick: (customer: Customer) => void;
}) {
  const [more, setMore] = useState(false);
  const focusFirst = useAutoFocusFirstField<HTMLDivElement>();
  const problem = newCustomerProblem(form);
  const add = useApiMutation<Customer, QuickCustomerInput>({
    mutationFn: createCustomer,
    invalidates: [customersKey],
    onSuccess: onPick,
  });

  return (
    <div ref={focusFirst} className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <Field label="Mobile" required>
          <input
            value={form.mobile}
            onChange={(event) => onForm({ ...form, mobile: event.target.value })}
            inputMode="tel"
            className={controlClass}
          />
        </Field>
        <Field label="Name" required>
          <input
            value={form.name}
            onChange={(event) => onForm({ ...form, name: event.target.value })}
            className={controlClass}
          />
        </Field>
      </div>
      <p className="text-default mb-0 text-[12px]">
        One number, one person. A number already on file is refused, naming who has it.
      </p>

      {more ? (
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label="City (optional)">
            <input
              value={form.city}
              onChange={(event) => onForm({ ...form, city: event.target.value })}
              className={controlClass}
            />
          </Field>
          <Field label="Email (optional)">
            <input
              type="email"
              value={form.email}
              onChange={(event) => onForm({ ...form, email: event.target.value })}
              className={controlClass}
            />
          </Field>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setMore(true)}
          className="text-primary min-h-11 cursor-pointer self-start border-0 bg-transparent p-0 font-semibold hover:underline"
        >
          + City and email (optional)
        </button>
      )}

      {problem && (form.name !== '' || form.mobile !== '') && (
        <p className="text-default mb-0 text-[13px]">{problem}</p>
      )}

      <Button
        variant="primary"
        shortcut="save"
        data-enter-key-commit
        disabled={problem !== null}
        loading={add.isPending}
        onClick={() => add.mutate(newCustomerInput(form))}
        className="h-12 w-full justify-center text-[15px]"
      >
        Add and continue
        <i className="icon icon-arrow-right" />
      </Button>
    </div>
  );
}

/** Text with the part that matched what was typed marked, so a result shows why it was found. */
function Marked({ text, term }: { text: string; term: string }) {
  return (
    <>
      {highlight(text, term).map((part, index) =>
        part.match ? (
          <mark key={index} className="bg-warning/20 rounded-[3px] text-inherit">
            {part.text}
          </mark>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

function rented(count: number): string {
  if (count === 0) {
    return 'first booking';
  }

  return `rented ${count} ${count === 1 ? 'time' : 'times'}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}
